package com.siriusxm.pia.views.mddb

import com.siriusxm.pia.TokenAccess
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.plugins.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlin.time.Clock
import kotlin.time.Instant

class MddbAPI(url: String, private val tokenAccess: TokenAccess) {
    private val parsedUrl = Url(url)

    private val httpClient = HttpClient {
        install(ContentNegotiation) {
            json(Json { isLenient = true; ignoreUnknownKeys = true })
        }
        defaultRequest {
            url {
                protocol = parsedUrl.protocol
                host = parsedUrl.host
                port = parsedUrl.port.takeIf { it != parsedUrl.protocol.defaultPort } ?: 0
                encodedPath = parsedUrl.encodedPath
            }
        }
        tokenAccess.installAuth(this)
    }

    suspend fun status(): String? {
        return try {
            httpClient.get("status").bodyAsText()
        } catch (e: Throwable) {
            console.log("status() had an exception: $e")
            null
        }
    }

    suspend fun statusAuthenticated(): String? {
        return try {
            httpClient.get("status_authenticated").bodyAsText()
        } catch (e: Throwable) {
            console.log("statusAuthenticated() had an exception: $e")
            null
        }
    }

    suspend fun enablePolling(entityType: String): JobInfo? {
        return try {
            httpClient.post("polling/$entityType/enable").body()
        } catch (e: Throwable) {
            console.log("enablePolling($entityType) had an exception: $e")
            null
        }
    }

    suspend fun disablePolling(entityType: String): JobInfo? {
        return try {
            httpClient.post("polling/$entityType/disable").body()
        } catch (e: Throwable) {
            console.log("disablePolling($entityType) had an exception: $e")
            null
        }
    }

    suspend fun pollingJobStatus(): JobStatusResponse? {
        return try {
            httpClient.get("polling/status").body()
        } catch (e: Throwable) {
            console.log("pollingJobStatus() had an exception: $e")
            null
        }
    }

    suspend fun triggerPolling(entityType: String): String? {
        return try {
            httpClient.post("polling/$entityType/trigger").bodyAsText()
        } catch (e: Throwable) {
            console.log("triggerPolling($entityType) had an exception: $e")
            null
        }
    }

    suspend fun fetchEntityById(typedEntityId: String): String? {
        return try {
            httpClient.get("entity/$typedEntityId").bodyAsText()
        } catch (e: Throwable) {
            console.log("fetchEntityById($typedEntityId) had an exception: $e")
            null
        }
    }

    suspend fun fetchRawEntity(typedEntityId: String): String? {
        return try {
            httpClient.get("entity/$typedEntityId/raw").bodyAsText()
        } catch (e: Throwable) {
            console.log("fetchRawEntityById($typedEntityId) had an exception: $e")
            null
        }
    }

    suspend fun fetchRawDetails(typedEntityId: String): RawReadDetailsResponse? {
        val response = httpClient.get("entity/$typedEntityId/rawDetails")
        if (response.status.isSuccess()) {
            return response.body()
        } else {
            val content = response.bodyAsText()
            throw ResponseException(response, content)
        }
    }

    suspend fun refreshEntity(typedEntityId: String): Boolean {
        return try {
            httpClient.post("entity/$typedEntityId/refresh").body<Unit>()
            true
        } catch (e: Throwable) {
            console.log("refreshEntityById($typedEntityId) had an exception: $e")
            false
        }
    }
}

@Serializable
data class RawReadDetailsResponse(
    val pollType: String,
    val data: List<MDDBRawResponse>?
)

@Serializable
data class MDDBRawResponse(val url: String,
                           val contentType: String,
                           val rawData: String)

@Serializable
data class JobInfo(
    val id: String,
    val type: String,
    val startTime: Instant? = null,
    val disabledUntil: Instant? = null
) {
    val disabled : Boolean
        get() = disabledUntil != null
                &&  Clock.System.now() < disabledUntil
}

@Serializable
data class JobStatusResponse(
    val jobs: List<JobInfo>? = null
)
